import { FunctionComponent, useEffect, useState } from 'react';
import { subWeeks } from 'date-fns';
import { Formik } from 'formik';
import * as vp from 'vopay';
import { Box, Select, TextField, DateRangePicker } from 'vopay';
import * as Yup from 'yup';
import useFetchPortalPartnerAccounts from '@/hooks/useApi/useFetchPortalPartnerAccounts';
import { Button } from 'UI';
import styles from './TransactionsSearchForm.module.scss';
import { useTranslation } from 'react-i18next';

type SearchTransactionsProps = {
  timeRange: [vp.Timestamp, vp.Timestamp] | undefined;
  setTimeRange: (event: [vp.Timestamp, vp.Timestamp] | undefined) => void;
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  onTransactionsSearch: (values: vp.ParentAccountTransactionsRequest) => void;
  handlers: {
    handleDate: (values: [vp.Timestamp, vp.Timestamp] | undefined) => void;
  };
  setShouldShowEndDateError?: (event: boolean) => void;
  defaultValues: ParentAccountTransactionsRequest;
};

type ParentAccountTransactionsRequest = {
  FromDate: string;
  ToDate: string;
  VopayAccountID?: string;
  Currency?: string;
  TransactionType?: string;
  Status?: string;
  ExportCSV?: boolean;
  TransactionID?: string;
};

type AccountIdItem = {
  label: string;
  value: string;
}[];

type FC = FunctionComponent<SearchTransactionsProps>;

const TransactionsSearchForm: FC = ({
  timeRange,
  setTimeRange,
  handlers: formHandlers,
  hasInvalidEndDate,
  shouldShowEndDateError,
  setShouldShowEndDateError,
  onTransactionsSearch,
  defaultValues,
}) => {
  const { accounts, fetchPortalPartnerAccounts } = useFetchPortalPartnerAccounts();
  const [accountIdItems, setAccountIdItems] = useState<AccountIdItem>([]);
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountTransactions.SearchForm' });
  const ACCOUNT_LIST_LIMIT = 50;
  const initialTimeRange: [vp.Timestamp, vp.Timestamp] = [
    subWeeks(new Date(), 1).getTime() as vp.Timestamp,
    new Date().getTime() as vp.Timestamp,
  ];

  useEffect(() => {
    formHandlers.handleDate(timeRange);
  }, [timeRange]);

  const currencyOptions = [
    { label: t('allOption'), value: '' },
    ...Object.entries(vp.CURRENCY_SELECT_OPTIONS).map(([label, value]) => ({ label, value })),
  ];

  const transactionOptions = [
    { label: t('allOption'), value: '' },
    ...Object.entries(vp.TRANSACTION_TYPE_SELECT_OPTIONS)
      .filter(([value]) => {
        return value !== 'All';
      })
      .map(([value, label]) => ({ label, value })),
  ];

  const statusOptions = [
    { label: t('allOption'), value: '' },
    ...Object.entries(vp.TRANSACTION_STATUS_SELECT_OPTIONS).map(([value, label]) => ({ label, value })),
  ];

  useEffect(() => {
    fetchPortalPartnerAccounts({
      Limit: ACCOUNT_LIST_LIMIT,
    });
  }, []);

  useEffect(() => {
    if (accounts.length > 0) {
      const allAccounts = accounts
        .map((account) => ({
          label: account.AccountName,
          value: account.APIAccountID,
        }))
        .filter((item) => item.label && item.value);

      setAccountIdItems([{ label: t('allOption'), value: '' }, ...allAccounts]);
    }
  }, [accounts, t]);

  const transactionsFormSchema = Yup.object().shape({
    FromDate: Yup.string().required(),
    ToDate: Yup.string().required(),
  });

  return (
    <Box ai="center">
      <Formik
        initialValues={defaultValues}
        validationSchema={transactionsFormSchema}
        validateOnChange
        validateOnBlur
        onSubmit={onTransactionsSearch}
        enableReinitialize
      >
        {({ values, handleChange, handleBlur, handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Box flx="row" height="auto" gap="sm" wrap="wrap">
                <Box height="auto">
                  <div className={styles.datePickerContainer}>
                    <DateRangePicker
                      onChange={setTimeRange}
                      initialTime={timeRange || initialTimeRange}
                      hasInvalidEndDate={hasInvalidEndDate}
                      shouldShowEndDateError={shouldShowEndDateError}
                      setShouldShowEndDateError={setShouldShowEndDateError}
                      triggerVariant="outlineShadow"
                    />
                  </div>
                </Box>
                <Box mb="sm" height="auto" width="200px">
                  <Select
                    version="2"
                    placeholder={t('currencyPlaceholder')}
                    options={currencyOptions}
                    name="Currency"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.Currency || ''}
                    sort={false}
                  />
                </Box>
                <Box mb="sm" height="auto" width="200px">
                  <Select
                    version="2"
                    placeholder={t('transactionTypePlaceholder')}
                    options={transactionOptions}
                    name="TransactionType"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.TransactionType || ''}
                    sort={false}
                  />
                </Box>
                <Box mb="sm" height="auto" width="200px">
                  <Select
                    version="2"
                    placeholder={t('transactionStatusPlaceholder')}
                    options={statusOptions}
                    name="Status"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.Status || ''}
                    sort={false}
                  />
                </Box>
                <Box mb="sm" height="auto" width="200px">
                  <Select
                    version="2"
                    placeholder={t('vopayAccountIdPlaceholder')}
                    options={accountIdItems}
                    name="VopayAccountID"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    filterable
                    onFilter={(e) => {
                      e.preventDefault();
                      e.debounce(() => {
                        fetchPortalPartnerAccounts({
                          ...(e.filter.length >= 3 && { SearchText: e.filter }),
                          Limit: ACCOUNT_LIST_LIMIT,
                        });
                      }, 1000);
                    }}
                    value={values.VopayAccountID || ''}
                    sort={false}
                  />
                </Box>
                <Box mb="sm" height="auto">
                  <TextField
                    key="TransactionID"
                    name="TransactionID"
                    placeholder={t('transactionIdPlaceholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.TransactionID}
                    variant="outlineShadow"
                  />
                </Box>
                <Box jc="fe" height="auto" gap="sm">
                  <Button id="account-transactions-search" colorScheme="primary" size="small" type="submit">
                    {t('searchButton')}
                  </Button>
                </Box>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default TransactionsSearchForm;
